import { ReportParameter } from "../../types/reportTypes";

/** Safely read a dot-notation path off an object. */
export const getNestedValue = (obj: any, path: string): any =>
  path.split(".").reduce((current, key) => current?.[key], obj);

/**
 * The stable identity of a popup-table row — the value of the parameter's
 * `apiEndpoint.value` field (which may be a dotted path).
 */
export const rowIdentityOf = (
  param: ReportParameter | undefined,
  item: any,
): any => {
  const idPath = param?.apiEndpoint?.value;
  if (!idPath) return item;
  return getNestedValue(item, idPath);
};

/**
 * Material React Table's `getRowId` for a popup table.
 *
 * MRT defaults a row's id to its INDEX in the currently loaded data. With
 * manual pagination that is actively wrong: `rowSelection` is keyed by those
 * ids, so a selection of `{ "0": true }` made on page 1 re-highlights whatever
 * happens to be row 0 on page 2 — a different record entirely. It also means a
 * genuine selection silently "moves" as the user pages around.
 *
 * Keying on the record's own identifier fixes both directions: the right row
 * highlights on whichever page it appears, and no other row is ever mistaken
 * for it. Selection state also survives paging, because the key no longer
 * depends on what is currently loaded.
 *
 * Falls back to the index only for a row with no resolvable identifier, which
 * keeps MRT's own default behaviour rather than collapsing every such row onto
 * one shared id.
 */
export const popupRowId = (
  param: ReportParameter | undefined,
  row: any,
  index: number,
): string => {
  const id = rowIdentityOf(param, row);
  return id === undefined || id === null || typeof id === "object"
    ? String(index)
    : String(id);
};
